import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { AppIconsType } from '@fc-shared/ui/icon/icons';
import {
  TOGGLE_BUTTON_DISABLED_CLASSES,
  TOGGLE_BUTTON_ICON_ONLY_SIZE_CLASSES,
  TOGGLE_BUTTON_SELECTED_CLASSES,
  TOGGLE_BUTTON_SIZE_CLASSES,
  TOGGLE_BUTTON_UNSELECTED_CLASSES,
  ToggleButtonActiveColor,
  ToggleButtonSize,
} from './toggle-button.model';

@Component({
  selector: 'fc-toggle-button',
  imports: [IconComponent],
  template: `
    @let toggleIcon = icon();
    @if (toggleIcon) {
      <fc-icon aria-hidden="true" [icon]="toggleIcon" [size]="20" />
    }
    <ng-content />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class:
      'relative z-1 flex items-center justify-center gap-4 rounded-lg text-sm font-normal [&_fc-icon]:!bg-current',
    '[class]': 'stateClasses()',
    '[class.active]': 'selected()',
    '[class.flex-1]': 'fullWidth()',
    '[attr.aria-disabled]': 'isDisabled() || null',
    '(click)': 'handleClick()',
  },
})
export class ToggleButtonComponent {
  elRef = inject(ElementRef);
  disabled = input(false);
  value = input<any>(null);
  activeColor = input<ToggleButtonActiveColor>('default');
  icon = input<AppIconsType>();
  iconOnly = input(false);
  selected = signal(false);
  size = signal<ToggleButtonSize>('md');
  fullWidth = signal(false);
  isGroupDisabled = signal(false);
  onSelect = output<any>();

  readonly isDisabled = computed(
    () => this.disabled() || this.isGroupDisabled(),
  );

  protected readonly stateClasses = computed(() => {
    const sizeClasses = this.iconOnly()
      ? TOGGLE_BUTTON_ICON_ONLY_SIZE_CLASSES[this.size()]
      : TOGGLE_BUTTON_SIZE_CLASSES[this.size()];
    return `${sizeClasses} ${this.stateColorClasses()}`;
  });

  private readonly stateColorClasses = computed(() => {
    if (this.isDisabled()) return TOGGLE_BUTTON_DISABLED_CLASSES;
    if (this.selected()) {
      return TOGGLE_BUTTON_SELECTED_CLASSES[this.activeColor()];
    }
    return TOGGLE_BUTTON_UNSELECTED_CLASSES;
  });

  protected handleClick(): void {
    if (this.isDisabled()) return;
    this.onSelect.emit(this.value());
  }
}
